'use client';

import { useId, useState, type CSSProperties } from 'react';

import { FlywayFilterCarousel } from '@/components/patterns/FlywayFilterCarousel';
import { CloseIcon } from '@/components/primitives/icons/CloseIcon';
import { InfoIcon } from '@/components/primitives/icons/InfoIcon';
import { VisuallyHidden } from '@/components/primitives/VisuallyHidden';
import {
  FLYWAYS_MAP_COLORS,
  FLYWAYS_MAP_FILTERS,
  FLYWAYS_MAP_INKS,
  FLYWAYS_MAP_LEGEND,
  FLYWAYS_MAP_PAN_ICON_PATH,
  FLYWAYS_MAP_SPECIES_COUNTS,
  type FlywaysMapFilterId,
} from '@/constants/flywaysMap';

import styles from './FlywaysMapMobileChrome.module.css';

import type { Messages } from '@/content/messages';

type FlywaysMapMobileMessages = Messages['intro']['flywaysMap'];

type FlywaysMapMobileNavProps = {
  filter: FlywaysMapFilterId;
  onFilterChange: (filter: FlywaysMapFilterId) => void;
  messages: FlywaysMapMobileMessages;
};

type FlywaysMapMobileOverlaysProps = {
  filter: FlywaysMapFilterId;
  messages: FlywaysMapMobileMessages;
};

function navColors(filter: FlywaysMapFilterId) {
  if (filter === 'all') {
    return {
      background: 'var(--color-ink)',
      foreground: 'var(--text-primary-inverse)',
    };
  }

  return {
    background: FLYWAYS_MAP_COLORS[filter],
    foreground: 'var(--text-primary)',
  };
}

/**
 * Compact flyway carousel navbar — prev/next, sliding title, colour transition.
 */
export function FlywaysMapMobileNav({
  filter,
  onFilterChange,
  messages,
}: FlywaysMapMobileNavProps) {
  return (
    <FlywayFilterCarousel
      filters={FLYWAYS_MAP_FILTERS}
      value={filter}
      onChange={onFilterChange}
      labels={messages.filters}
      previousLabel={messages.previousFlyway}
      nextLabel={messages.nextFlyway}
      getColors={navColors}
      variant="inverse"
      className={styles.nav}
      prevDataAttrs={{ 'data-flyways-filter-nav': 'prev' }}
      nextDataAttrs={{ 'data-flyways-filter-nav': 'next' }}
      getTitleDataAttrs={(id) => ({ 'data-flyways-filter-title': id })}
    />
  );
}

/**
 * Compact map overlays: info toggle + species/legend popover + pan hint.
 */
export function FlywaysMapMobileOverlays({
  filter,
  messages,
}: FlywaysMapMobileOverlaysProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const popoverId = useId();
  const showInfo = filter !== 'all';

  const flywayColor =
    filter !== 'all' ? FLYWAYS_MAP_COLORS[filter] : undefined;
  const flywayInk =
    filter !== 'all' ? FLYWAYS_MAP_INKS[filter] : undefined;
  const legendItems = filter !== 'all' ? FLYWAYS_MAP_LEGEND[filter] : null;
  const speciesCount =
    filter !== 'all' ? FLYWAYS_MAP_SPECIES_COUNTS[filter] : null;

  return (
    <>
      {showInfo ? (
        <button
          type="button"
          className={styles.infoButton}
          aria-expanded={infoOpen}
          aria-controls={infoOpen ? popoverId : undefined}
          aria-label={infoOpen ? messages.closeInfo : messages.openInfo}
          onClick={() => {
            setInfoOpen((open) => !open);
          }}
        >
          {infoOpen ? <CloseIcon size={20} /> : <InfoIcon size={20} />}
        </button>
      ) : null}

      {infoOpen &&
      showInfo &&
      flywayColor &&
      flywayInk &&
      legendItems &&
      speciesCount != null ? (
        <div
          id={popoverId}
          className={styles.popover}
          role="dialog"
          aria-label={messages.badgeLabel}
        >
          <div
            className={styles.popoverHeader}
            style={
              {
                '--popover-accent': flywayColor,
                '--popover-ink': flywayInk,
              } as CSSProperties
            }
          >
            <p className={styles.popoverCount}>{speciesCount}</p>
            <p className={styles.popoverBadgeLabel}>{messages.badgeLabel}</p>
          </div>
          <ul className={styles.legendList}>
            {legendItems.map((item) => (
              <li key={item.id} className={styles.legendItem}>
                <span
                  className={styles.legendSwatch}
                  data-pattern={item.pattern}
                  style={
                    {
                      '--legend-color': flywayColor,
                    } as CSSProperties
                  }
                  aria-hidden
                />
                <span className={styles.legendLabel}>
                  {messages.legendItems[item.labelKey]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.panHint} aria-hidden>
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative SVG (ADR-004) */}
        <img
          src={FLYWAYS_MAP_PAN_ICON_PATH}
          alt=""
          width={48}
          height={48}
          className={styles.panHintImg}
          decoding="async"
        />
      </div>
      <VisuallyHidden>{messages.panHint}</VisuallyHidden>
    </>
  );
}
